/**
 * Created by katarinababic on 8.3.23.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { PortraitCard } from './ExploreCards/PortraitCard';
import { useTrans } from '../Store/System/LocalizationStore';
import { useLocalized } from '../Hooks/useLocalized';
import { Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import { Theme } from '../Theme/Theme';
import { KinasticContent } from '../Model/Explore/ExploreContentBuilder';
import { useExploreEntriesQuery, useExploreCategoryByIdentifier } from '../Hooks/useExploreQueries';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 48px 0;
  margin-top: 48px;
  position: relative;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${Theme.Colors.primary};
  }

  @media (max-width: 768px) {
    padding: 32px 16px;
    margin-top: 32px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconContainer = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${Theme.Colors.primary}20, ${Theme.Colors.primary}10);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const Title = styled.h3`
  color: #1a1a2e;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const SeeAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${Theme.Colors.primary};
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 24px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    background: ${Theme.Colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${Theme.Colors.primary}40;
  }

  i {
    font-size: 14px;
    transition: transform 0.2s ease;
  }

  &:hover i {
    transform: translateX(3px);
  }
`;

const ScrollContainer = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -16px;
    bottom: 0;
    width: 30px;
    background: linear-gradient(to left, rgba(233, 236, 239, 1) 0%, transparent 50%);
    pointer-events: none;
    z-index: 1;
  }
`;

const CardsGrid = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  overflow-y: visible;
  padding: 16px;
  padding-bottom: 32px;
  margin: -16px;
  margin-bottom: -32px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CardWrapper = styled.div`
  flex: 0 0 220px;
  scroll-snap-align: start;

  @media (max-width: 768px) {
    flex: 0 0 180px;
  }
`;

export type RelatedEntriesViewProps = {
  entry: KinasticContent;
  linkPrefix?: string;
};

export const RelatedEntriesView: React.FC<RelatedEntriesViewProps> = observer(({ entry, linkPrefix }) => {
  const { t } = useTrans();

  // V3 entries link to parents via `parentIds` (UUIDs); V1's `categories`
  // (slug identifiers) no longer exists on the entry shape.
  const parentIds = 'parentIds' in entry ? entry.parentIds : [];
  const { data: entries = [] } = useExploreEntriesQuery(
    { categories: parentIds, size: 20 },
    { enabled: parentIds.length > 0 },
  );
  const relatedEntries = entries.filter((e) => e.id !== entry.id);
  const { data: category } = useExploreCategoryByIdentifier(parentIds[0]);
  const categoryTitle = useLocalized(category?.name);

  const defaultTitle = t('relatedEntriesView.title', { defaultValue: 'Related content' });

  if (relatedEntries.length === 0) {
    return null;
  }

  return (
    <StyledContainer>
      <Container>
        <HeaderSection>
          <TitleWrapper>
            <IconContainer>✨</IconContainer>
            <Title>{categoryTitle || defaultTitle}</Title>
          </TitleWrapper>
          <SeeAllLink to={category ? `${linkPrefix}${category?.identifier}` : `${linkPrefix}`}>
            {t('exploreCategoryIntroCard.seeAllButton', { defaultValue: 'See all' })}
            <i className="bi bi-arrow-right" />
          </SeeAllLink>
        </HeaderSection>
        <ScrollContainer>
          <CardsGrid>
            {relatedEntries.map((e) => (
              <CardWrapper key={e.id}>
                <PortraitCard entry={e} linkPrefix={linkPrefix} />
              </CardWrapper>
            ))}
          </CardsGrid>
        </ScrollContainer>
      </Container>
    </StyledContainer>
  );
});
